import React, { Component } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";
import { mediaQueries } from "../styles/media.js";
import Question, { Optional } from "./Question";
import Label from "./Label";
import Hint from "./Hint";
import ErrorMessage from "./ErrorMessage";
import { AttachedListItem, AttachIcon } from "./AttachedFile";
import Dropzone from "react-dropzone";
import { AttachFile, HighlightOff } from "@styled-icons/material";
import { Error as ErrorIcon } from "@styled-icons/material";

export const allowedFileTypes = ".pdf,.png,.tiff,.jpeg,.jpg,.txt";

const UndoText = styled.p`
  color: var(--juno-color-text-secondary);
  margin: 0;
  font-size: 1em;
`;

const DropzoneContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border-color: var(--juno-color-border);
  border-width: 2px;
  border-radius: 16px;
  border-style: dashed;
  background-color: #fafafa;
  color: var(--juno-color-text-secondary);
  outline: none;
  transition: border 0.24s ease-in-out;
  &:hover {
    color: #2196f3;
    border-color: #2196f3;
    cursor: pointer;
  }
`;

const InstructionText = styled.p`
  color: var(--juno-color-text-secondary);
  text-align: center;

  &:hover {
    color: var(--juno-color-link-hover);
  }
`;

const ProgressBar = styled.div`
  max-width: 38rem;
  background: var(--juno-color-pale-grey);
  border-radius: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  margin-top: 0.5em;
`;

const ProgressBarStatus = styled.div`
  @keyframes expand {
    0% {
      width: 0;
    }
    100% {
      width: 100%;
    }
  }

  background: var(--juno-color-green);
  height: 2px;
  margin: 4px 10px;

  animation: expand 3s linear infinite;
`;

const AttachedTitle = styled.h3`
  font-size: var(--juno-font-size-h4-sm);
  margin: 0.5em 0 0.25em 0;
  padding: 0;
  color: ${(props) => (props.uploading ? "var(--juno-color-grey)" : "var(--juno-color-green)")};
`;

const AttachedList = styled.ul`
  color: var(--juno-color-text);
  font-family: var(--juno-font-family-body);
  font-size: var(--juno-font-size-body-sm);
  line-height: 1.5;

  margin-top: 0;
  margin-bottom: 1em;
  ${mediaQueries.md} {
    font-size: var(--juno-font-size-body-md);
    line-height: 1.75;
  }
  list-style-type: none;
  padding-left: 0;
`;

const RemoveIcon = styled.div`
  padding-top: 3px;
  margin-left: 12px;
  font-size: 14px;
  color: black;
  &:hover {
    color: red;
    cursor: pointer;
  }
`;

// Seeds the display list on mount only, like defaultValue. The component owns the list
// after that and reports every change through onUploadsChanged.
const restoreUploads = (defaultUploads) =>
  (defaultUploads || []).map((upload) => ({
    file_id: uuidv4(),
    upload_id: upload.upload_id,
    filename: upload.filename,
    uploading: false,
    failed: false,
  }));

class FileUploadQuestion extends Component {
  constructor(props) {
    super(props);

    this.state = {
      uploads: restoreUploads(props.defaultUploads),
      uploading: false,
      errorMessage: null,
    };
    this.uploadControllers = new Map();
  }

  componentDidUpdate(prevProps, prevState) {
    const uploading = this.state.uploads.some((u) => u.uploading);

    if (uploading !== this.state.uploading) {
      this.setState({ uploading: uploading });
      if (this.props.onUploadInProgress) {
        this.props.onUploadInProgress(uploading);
      }
    }

    const hadFailures = prevState.uploads.some((u) => u.failed);
    const hasFailures = this.state.uploads.some((u) => u.failed);
    // Failed rows are omitted from onUploadsChanged, so report their aggregate state separately.
    if (hasFailures !== hadFailures && this.props.onUploadFailuresChanged) {
      this.props.onUploadFailuresChanged(hasFailures);
    }
  }

  notifyUploadsChanged = () => {
    this.props.onUploadsChanged(
      this.state.uploads.filter((u) => !!u.upload_id).map(({ upload_id, filename }) => ({ upload_id, filename })),
    );
  };

  updateUploadDetails = (file_id, newDetails) => {
    // Notify parent if an upload has been completed successfully (upload_id being set)
    const callback = !!newDetails.upload_id ? this.notifyUploadsChanged : null;

    this.setState((state) => {
      const newUploads = [...state.uploads];
      const index = newUploads.findIndex((u) => u.file_id === file_id);
      if (index < 0) {
        // file has been removed since the upload was started
        return {};
      }
      newUploads[index] = { ...newUploads[index], ...newDetails };
      return {
        uploads: newUploads,
      };
    }, callback);
  };

  uploadFile = (upload) => {
    const controller = new AbortController();
    this.uploadControllers.set(upload.file_id, controller);

    const formData = new FormData();
    formData.append("file", upload.file, upload.file.name);

    const uploadUrl = this.props.uploadUrl || "/api/dashboard/upload_file";
    fetch(uploadUrl, {
      method: "POST",
      credentials: "same-origin",
      signal: controller.signal,
      body: formData,
    })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        return resp.json();
      })
      .then((data) => {
        if (data == null || typeof data.upload_id !== "string" || data.upload_id.trim() === "") {
          throw new Error("Upload response did not include an upload ID");
        }
        this.uploadControllers.delete(upload.file_id);
        this.updateUploadDetails(upload.file_id, {
          upload_id: data.upload_id,
          uploading: false,
          // Release the File. The row shows filename, so nothing reads the bytes again.
          file: null,
        });
      })
      .catch((err) => {
        this.uploadControllers.delete(upload.file_id);
        if (err.name === "AbortError") return;
        console.error("File upload failed:", err);
        Sentry.captureException(err, {
          tags: { action: "FileUpload", caseRef: this.props.caseRef, doableId: this.props.doableId },
        });
        this.updateUploadDetails(upload.file_id, {
          uploading: false,
          failed: true,
        });
      });
  };

  componentWillUnmount() {
    this.uploadControllers.forEach((controller) => controller.abort());

    // The uploads stop here, so tell the parent they stopped. A shown_when condition can hide
    // this question while a file is in flight. Without this, the parent waits for an upload
    // that no longer runs, and keeps its submit button disabled.
    if (this.state.uploading && this.props.onUploadInProgress) {
      this.props.onUploadInProgress(false);
    }

    // React StrictMode also unmounts a fresh component, so only clear a failure that exists.
    if (this.state.uploads.some((u) => u.failed) && this.props.onUploadFailuresChanged) {
      this.props.onUploadFailuresChanged(false);
    }
  }

  dropAccepted = (files) => {
    // Clear any existing error messages
    this.setState({ errorMessage: null });

    const validFiles = files.filter((file) => file.size > 0);

    // Check if any files were rejected due to size
    if (validFiles.length < files.length) {
      // Set an error message for zero-byte files
      this.setState({
        errorMessage: "Zero byte files cannot be uploaded.",
      });
      // If no valid files, exit early
      if (validFiles.length === 0) {
        return;
      }
    }

    const addedUploads = validFiles.map((file) => ({
      file_id: uuidv4(),
      file: file,
      filename: file.name,
      uploading: true,
      failed: false,
    }));

    const replacingFile = !this.props.multiple && this.state.uploads.length > 0;

    this.setState(
      (state, props) => {
        if (!props.multiple && state.uploads.length > 0) {
          // we're replacing an existing upload — abort any in-flight requests
          state.uploads.forEach((u) => {
            const controller = this.uploadControllers.get(u.file_id);
            if (controller) {
              controller.abort();
              this.uploadControllers.delete(u.file_id);
            }
          });
        }
        const newUploads = props.multiple ? [...state.uploads] : [];
        newUploads.push(...addedUploads);
        return {
          uploads: newUploads,
          // Use state.errorMessage (not this.state) to respect React 18 batching
          errorMessage: state.errorMessage || null,
        };
      },
      replacingFile ? this.notifyUploadsChanged : undefined,
    );

    addedUploads.forEach((upload) => this.uploadFile(upload));
  };

  dropRejected = (files) => {
    this.setState({
      errorMessage: `File format must be ${allowedFileTypes} and less than 20M`,
    });
  };

  removeFile = (file_id) => (event) => {
    // prevent the DropZone click handler opening the file dialog when remove
    // is clicked
    event.stopPropagation();

    const controller = this.uploadControllers.get(file_id);
    if (controller) {
      controller.abort();
      this.uploadControllers.delete(file_id);
    }

    this.setState((prevState) => {
      let newUploads = [...prevState.uploads];
      const index = newUploads.findIndex((u) => u.file_id === file_id);
      if (index < 0) {
        // Upload not present - should never happen...
        return {};
      }

      newUploads.splice(index, 1);
      return {
        uploads: newUploads,
      };
    }, this.notifyUploadsChanged);
  };

  render() {
    let uploadInfo = this.state.uploads.map((upload) => {
      if (upload.uploading) {
        return (
          <ProgressBar key={`progress_${upload.file_id}`} data-testid={`${upload.filename}-progressbar`}>
            <ProgressBarStatus />
          </ProgressBar>
        );
      }

      if (upload.failed) {
        return (
          <AttachedListItem key={`failed_${upload.file_id}`} error={true}>
            <AttachIcon error={true}>
              <ErrorIcon
                size="24"
                onClick={this.removeFile(upload.file_id)}
                data-testid={`${upload.filename}-remove`}
              />
            </AttachIcon>
            <span error="true">failed, please try again — {upload.filename}</span>
          </AttachedListItem>
        );
      }

      return (
        <AttachedListItem key={upload.file_id}>
          <AttachIcon>
            <AttachFile size="24" />
          </AttachIcon>
          <span>{upload.filename}</span>
          <RemoveIcon>
            <HighlightOff
              size="24"
              onClick={this.removeFile(upload.file_id)}
              data-testid={`${upload.filename}-remove`}
            />
          </RemoveIcon>
        </AttachedListItem>
      );
    });

    const uploadedCount = this.state.uploads.filter((u) => !!u.upload_id).length;
    return (
      <Question>
        <Label>
          {this.props.question} {this.props.optional && <Optional />}
          {this.props.hint && <Hint>{this.props.hint}</Hint>}
          {this.props.validationError && <ErrorMessage>{this.props.validationError}</ErrorMessage>}
          {this.state.errorMessage && <ErrorMessage>{this.state.errorMessage}</ErrorMessage>}
        </Label>

        {uploadInfo.length > 0 && (
          <>
            {this.state.uploading && <AttachedTitle uploading={true}>Uploading...</AttachedTitle>}
            {!this.state.uploading && uploadedCount > 0 && (
              <AttachedTitle>Thanks, {uploadedCount > 1 ? "these are" : "that's"} now ready to submit:</AttachedTitle>
            )}
            <AttachedList>{uploadInfo}</AttachedList>
          </>
        )}

        <Dropzone
          onDropAccepted={this.dropAccepted}
          onDropRejected={this.dropRejected}
          multiple={!!this.props.multiple}
          accept={allowedFileTypes}
          maxSize={20971520}
        >
          {({ getRootProps, getInputProps }) => (
            <>
              <DropzoneContent
                {...getRootProps({
                  className: "dropzone",
                })}
              >
                <input {...getInputProps()} />

                {uploadInfo.length > 0 && (
                  <div>
                    {!this.props.multiple && <UndoText>Not the right one? Click to select a new file</UndoText>}
                  </div>
                )}

                {(uploadInfo.length === 0 || this.props.multiple) && (
                  <div>
                    <InstructionText>
                      {`Drop ${uploadInfo.length > 0 ? "more " : ""}${this.props.multiple ? "files" : "a file"} here, or click to select a file`}
                    </InstructionText>
                  </div>
                )}
              </DropzoneContent>
            </>
          )}
        </Dropzone>
      </Question>
    );
  }
}

FileUploadQuestion.propTypes = {
  question: PropTypes.string.isRequired,
  hint: PropTypes.string,
  optional: PropTypes.bool,
  multiple: PropTypes.bool,
  validationError: PropTypes.string,
  // Defaults to /api/dashboard/upload_file. Anonymous requests pass their own endpoint.
  uploadUrl: PropTypes.string,
  // Reported to Sentry when an upload fails.
  caseRef: PropTypes.string,
  doableId: PropTypes.string,
  // filename is required because a restored entry has no File object to read a name from.
  // Without it the list shows a blank row and an unlabelled remove button.
  defaultUploads: PropTypes.arrayOf(
    PropTypes.shape({
      upload_id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired,
    }),
  ),
  onUploadsChanged: PropTypes.func.isRequired,
  onUploadInProgress: PropTypes.func,
  onUploadFailuresChanged: PropTypes.func,
};

export default FileUploadQuestion;
